import {
  getSettings,
  getFilters,
  getLocations,
  getSearch,
  getBrands,
  getSources,
  getDistributions,
  getZones,
  getAnalytics,
} from '@/services/radar.service'
import { getGeoJson } from '@/services/geojson.service'
import { reducer } from '@/utils/formatters.util'
import { getSubDivisions } from '../../services/radar.service'

const SET_CLIENT_ID = 'SET_CLIENT_ID'
const SET_CURRENT_LOCATION = 'SET_CURRENT_LOCATION'
const SET_CURRENT_ZONE = 'SET_CURRENT_ZONE'
const SET_LOCATIONS = 'SET_LOCATIONS'
const SET_SEARCH = 'SET_SEARCH'
const SET_SOURCES = 'SET_SOURCES'
const SET_FILTERS = 'SET_FILTERS'
const SET_DISTRIBUTIONS = 'SET_DISTRIBUTIONS'
const SET_SETTINGS = 'SET_SETTINGS'
const SET_BRANDS = 'SET_BRANDS'
const SET_BRANDS_GROUPS = 'SET_BRANDS_GROUPS'
const SET_ZONES = 'SET_ZONES'
const SET_COMPARE_ZONES = 'SET_COMPARE_ZONES'
const SET_GEO_JSONS = 'SET_GEO_JSONS'
const SET_SAVE_GEO_JSONS = 'SET_SAVE_GEO_JSONS'
const SET_SUB_DIVISIONS = 'SET_SUB_DIVISIONS'
const SET_ANALYTICS = 'SET_ANALYTICS'

const NUMBER_GEOJSONS_BEFORE_CLEAN = 150

const state = () => {
  return {
    clientId: null,
    currentLocation: null,
    currentZone: null,
    locations: null,
    search: null,
    settings: null,
    brands: null,
    brandsGroup: null,
    filters: null,
    distributions: null,
    sources: null,
    zones: null,
    compareZones: null,
    geoJsons: [],
    saveGeoJson: [],
    subDivisions: null,
    analytics: null,
  }
}

const mutations = {
  [SET_CLIENT_ID]: (state, clientId) => {
    state.clientId = clientId
  },
  [SET_CURRENT_LOCATION]: (state, location) => {
    state.currentLocation = location
  },
  [SET_CURRENT_ZONE]: (state, zone) => {
    state.currentZone = zone
  },
  [SET_LOCATIONS]: (state, locations) => {
    state.locations = locations
  },
  [SET_SEARCH]: (state, search) => {
    state.search = search
  },
  [SET_SETTINGS]: (state, settings) => {
    state.settings = settings
  },
  [SET_BRANDS]: (state, brands) => {
    state.brands = brands
  },
  [SET_BRANDS_GROUPS]: (state, brandsGroup) => {
    state.brandsGroup = brandsGroup
  },
  [SET_FILTERS]: (state, filters) => {
    state.filters = filters
  },
  [SET_DISTRIBUTIONS]: (state, distributions) => {
    state.distributions = distributions
  },
  [SET_SOURCES]: (state, sources) => {
    state.sources = sources
  },
  [SET_ZONES]: (state, zones) => {
    state.zones = zones
  },
  [SET_COMPARE_ZONES]: (state, zones) => {
    state.compareZones = zones
  },
  [SET_GEO_JSONS]: (state, geoJsons) => {
    state.geoJsons = geoJsons
  },
  [SET_SAVE_GEO_JSONS]: (state, { newGeoJsonSave, currentCountries }) => {
    let newSave = []
    if (newGeoJsonSave.length > NUMBER_GEOJSONS_BEFORE_CLEAN) {
      newGeoJsonSave.forEach(geoJson => {
        try {
          const country = geoJson.features[0].properties.country
          if (currentCountries.includes(country)) {
            newSave.push(geoJson)
          }
        } catch (e) {
          newSave.push(geoJson)
          console.error(`The following geosjon as no country: ${geoJson.features[0].properties.name}\n${e}`)
        }
      })
      state.saveGeoJson = newSave
    } else {
      state.saveGeoJson = newGeoJsonSave
    }
  },
  [SET_SUB_DIVISIONS]: (state, subDivisions) => {
    state.subDivisions = subDivisions
  },
  [SET_ANALYTICS]: (state, analytics) => {
    state.analytics = analytics
  },
}

const actions = {
  setClientId: ({ commit }, clientId) => {
    commit(SET_CLIENT_ID, clientId)
  },
  setCurrentLocation: ({ commit }, location) => {
    commit(SET_CURRENT_LOCATION, location)
  },
  setCurrentZone: ({ commit }, zone) => {
    commit(SET_CURRENT_ZONE, zone)
  },
  setZones: ({ commit }, zone) => {
    commit(SET_ZONES, zone)
  },
  setGeoJsonSave: ({ commit }, geoJsonSave) => {
    commit(SET_SAVE_GEO_JSONS, geoJsonSave)
  },
  setBrandsGroup: ({ commit }, brands) => {
    const defaultBrand = brands.find(brandObj => brandObj.brand.brand_status === true).brand
    commit(SET_BRANDS_GROUPS, defaultBrand.groups.length > 0 ? defaultBrand : null)
  },
  getClientSettings: ({ commit, state }, urlParams) => {
    return getSettings(state.clientId, urlParams).then(settings => {
      commit(SET_SETTINGS, settings)
      if (Object.keys(settings).length === 0) {
        return Promise.reject()
      }
      return Promise.resolve(state.settings)
    })
  },
  getClientSubDivisions: ({ commit, state }, urlParams = '') => {
    const subDivisionsPromises = state.settings.map(setting => getSubDivisions(state.clientId, setting.key, urlParams))
    return Promise.all(subDivisionsPromises).then(subDivisions => {
      commit(SET_SUB_DIVISIONS, reducer(subDivisions))
      return Promise.resolve()
    })
  },
  getClientFilters: ({ commit, state }, urlParams) => {
    const filtersPromises = state.settings.map(setting => getFilters(state.clientId, setting.key, urlParams))
    return Promise.all(filtersPromises).then(filters => {
      commit(SET_FILTERS, reducer(filters))
      return Promise.resolve()
    })
  },
  getClientBrands: ({ dispatch, commit, state, rootState }, { route, urlParams }) => {
    const withCompetitor = route.query.competitor === '0' ? false : true
    const brandsPromises = state.settings.map(setting =>
      getBrands(state.clientId, setting.key, +withCompetitor, urlParams)
    )
    return Promise.all(brandsPromises).then(brands => {
      const reduceBrand = reducer(brands)
      commit(SET_BRANDS, reduceBrand)
      dispatch('setBrandsGroup', reduceBrand[rootState.refine.currentKpi].brands)
      return Promise.resolve()
    })
  },
  getClientSearch: ({ commit, state }, urlParams) => {
    const searchPromises = state.settings.map(setting => getSearch(state.clientId, setting.key, urlParams))
    return Promise.all(searchPromises).then(search => {
      commit(SET_SEARCH, reducer(search))
      return Promise.resolve()
    })
  },
  getClientSources: ({ commit, state }, { setting, urlParams }) => {
    return getSources(state.clientId, setting.key, urlParams).then(sources => {
      commit(SET_SOURCES, sources)
      return Promise.resolve()
    })
  },
  getClientDistributions: ({ commit, state }, { setting, urlParams }) => {
    return getDistributions(state.clientId, setting.key, urlParams).then(distributions => {
      commit(SET_DISTRIBUTIONS, distributions)
      return Promise.resolve()
    })
  },
  getClientLocations: ({ commit, state }, { setting, urlParams }) => {
    return getLocations(state.clientId, setting.key, urlParams).then(locations => {
      commit(SET_LOCATIONS, locations)
      return Promise.resolve()
    })
  },
  getClientAnalytics: ({ commit, state }, { setting, urlParams }) => {
    return getAnalytics(state.clientId, setting.key, urlParams).then(analytics => {
      commit(SET_ANALYTICS, analytics)
      return Promise.resolve()
    })
  },
  getCompareZones: ({ commit, state }, { setting, urlParams }) => {
    return getZones(state.clientId, setting.key, urlParams).then(zones => {
      commit(SET_COMPARE_ZONES, zones)
      return Promise.resolve()
    })
  },
  getReturnZones: ({ state }, { setting, urlParams }) => {
    return getZones(state.clientId, setting.key, urlParams).then(zones => {
      return zones
    })
  },
  getClientGeoJsons: ({ commit, state, rootState }, zones) => {
    let geoJsonInData = []
    let urls = []
    const saveGeoJson = state.saveGeoJson

    zones.map(zone => {
      let fund = 0
      saveGeoJson.forEach(geoJson => {
        if (geoJson) {
          if (zone.geojson === geoJson.features[0].properties.id) {
            fund = 1
            geoJsonInData.push(geoJson)
          }
        }
      })
      if (!fund) {
        const countryCode = zone.geojson.split('-').shift()
        if (zone.geojson) urls.push(`${countryCode}/${zone.geojson}`)
        else console.error(`The following zone missed geojson.\nName: ${zone.name.default}\nKey: ${zone.key}`)
      }
    })
    const promises = urls.map(url => getGeoJson(url))
    return Promise.all(promises).then(geojsons => {
      let newGeoJsonSave = saveGeoJson.concat(geojsons.filter(b => !saveGeoJson.includes(b)))
      commit(SET_SAVE_GEO_JSONS, { newGeoJsonSave, currentCountries: rootState.refine.currentCountries })
      const geoJsonMerge = [...geojsons, ...geoJsonInData]
      commit(SET_GEO_JSONS, geoJsonMerge)
    })
  },
}

const getters = {
  visibleLocations: (state, getters, rootState) => {
    if (rootState.radar.currentRoute === 'markersIntention') return state.locations[rootState.refine.currentKpi]

    const bounds = rootState.radar.map.bounds
    if (bounds && state.locations[rootState.refine.currentKpi]) {
      const markersDistances = []
      state.locations[rootState.refine.currentKpi].forEach(location => {
        if (
          location._geoloc.lat <= bounds.getNorthEast().lat() &&
          location._geoloc.lat >= bounds.getSouthWest().lat() &&
          location._geoloc.lng <= bounds.getNorthEast().lng() &&
          location._geoloc.lng >= bounds.getSouthWest().lng()
        ) {
          // eslint-disable-next-line no-undef
          const locationLatLng = new google.maps.LatLng(location._geoloc)
          // eslint-disable-next-line no-undef
          const distance = google.maps.geometry.spherical.computeDistanceBetween(locationLatLng, bounds.getCenter())
          markersDistances.push({
            distance,
            location,
          })
        }
      })

      const closestMarkers = markersDistances.sort((a, b) => a.distance - b.distance)
      return closestMarkers.map(marker => {
        return marker.location
      })
    }
  },
  activeSetting: (state, getters, rootState) => {
    return state.settings.filter(setting => setting.key === rootState.refine.currentKpi)[0]
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
